import type {
  MeetingTemplateColor,
  MeetingTemplateScope,
} from '@wundo/shared/meetingTemplate.ts';
import {
  DEFAULT_MEETING_TEMPLATE_VIEW_LABELS,
  type MeetingTemplateViewLabels,
} from './meeting-template-view-labels.ts';

interface TopicPlace {
  position: number;
  total: number;
}

/* The editor draws the read-only view too, so it carries the view's labels. */
export interface MeetingTemplateEditorLabels extends MeetingTemplateViewLabels {
  title: string;
  nameLabel: string;
  namePlaceholder: string;
  descriptionLabel: string;
  descriptionHint: string;
  descriptionPlaceholder: string;
  colorLabel: string;
  colorHint: string;
  kindHint: string;
  scopeLabel: string;
  scopeHint: string;
  /** Who a new template is for: the whole organisation, or its writer alone. */
  scopes: Record<MeetingTemplateScope, string>;
  /** A colour has no text of its own. */
  colors: Record<MeetingTemplateColor, string>;
  audienceLabel: string;
  audienceHint: string;
  /** The option that aims it at nobody in particular. */
  audienceNone: string;
  /** The option that aims it at a whole track, in that track's group. */
  audienceWholeTrack: (params: { title: string }) => string;
  /** The option that aims it at one cohort. */
  audienceOnlyCohort: (params: { title: string }) => string;
  /** The group of cohorts that are on no track. */
  audienceOtherCohorts: string;
  /** In place of the field, on the template no track or cohort can narrow. */
  audienceDefaultNote: string;
  topicsHint: string;
  topicTitleLabel: (params: { position: number }) => string;
  topicTitlePlaceholder: string;
  topicDescriptionLabel: (params: { position: number }) => string;
  topicDescriptionPlaceholder: string;
  addTopic: string;
  /** Names the grip a topic is dragged by. */
  moveTopic: (params: { position: number }) => string;
  removeTopic: (params: { position: number }) => string;
  topicDragInstructions: string;
  topicDragStart: (params: { position: number }) => string;
  /** Also what a keyboard move announces. */
  topicDragOver: (params: TopicPlace) => string;
  topicDragEnd: (params: TopicPlace) => string;
  topicDragCancel: string;
  save: string;
  create: string;
  delete: string;
  deleteTitle: string;
  deleteDescription: (params: { name: string }) => string;
  deleteConfirm: string;
  deleteCancel: string;
}

export const DEFAULT_MEETING_TEMPLATE_EDITOR_LABELS: MeetingTemplateEditorLabels = {
  ...DEFAULT_MEETING_TEMPLATE_VIEW_LABELS,
  title: 'Template',
  nameLabel: 'Name',
  namePlaceholder: 'E.g. Peer coaching',
  descriptionLabel: 'Description',
  descriptionHint:
    'Optional. A short explanation of what this conversation type is about.',
  descriptionPlaceholder: 'E.g. A short conversation about how the past month went.',
  colorLabel: 'Colour',
  colorHint: 'The colour this conversation type wears everywhere.',
  kindHint: 'Which meetings this type is for. This cannot be changed later.',
  scopeLabel: 'Share',
  scopeHint:
    'Whether everyone in your organisation can use this type. This cannot be changed later.',
  scopes: {
    organisation: 'For the whole organisation',
    personal: 'Just for me',
  },
  colors: {
    navy: 'Navy',
    green: 'Green',
    pink: 'Pink',
    purple: 'Purple',
    teal: 'Teal',
    grey: 'Grey',
  },
  audienceLabel: 'Audience',
  audienceHint: 'Who is offered this conversation type.',
  audienceNone: 'Whole organisation',
  audienceWholeTrack: ({ title }) => `Whole track: ${title}`,
  audienceOnlyCohort: ({ title }) => `Only cohort: ${title}`,
  audienceOtherCohorts: 'Cohorts without a track',
  audienceDefaultNote: 'A default always applies to the whole organisation.',
  topicsHint:
    'The fixed structure of every conversation of this type. Each topic becomes a section in your preparation and in your report.',
  topicTitleLabel: ({ position }) => `Title of topic ${position}`,
  topicTitlePlaceholder: 'E.g. Results',
  topicDescriptionLabel: ({ position }) => `Description of topic ${position}`,
  topicDescriptionPlaceholder: 'What is this topic about?',
  addTopic: 'Add topic',
  moveTopic: ({ position }) => `Move topic ${position}`,
  removeTopic: ({ position }) => `Remove topic ${position}`,
  topicDragInstructions: 'Use the up and down arrows to move this topic.',
  topicDragStart: ({ position }) => `Topic ${position} picked up.`,
  topicDragOver: ({ position, total }) =>
    `Topic is now in position ${position} of ${total}.`,
  topicDragEnd: ({ position, total }) =>
    `Topic dropped in position ${position} of ${total}.`,
  topicDragCancel: 'Move of topic cancelled.',
  save: 'Save changes',
  create: 'Create template',
  delete: 'Delete template',
  deleteTitle: 'Delete this template?',
  deleteDescription: ({ name }) => `${name} will be deleted. This cannot be undone.`,
  deleteConfirm: 'Delete',
  deleteCancel: 'Cancel',
};
